// Align Fabric's caret math with paint by disabling kerning on every 2D
// canvas context. Fabric sums per-character measureText() advances (un-kerned)
// to position the caret while the browser paints fillText() with kerning
// applied, which causes the caret to drift inside condensed display faces
// like BebasNeuePro. Forcing fontKerning='none' makes paint match measurement.
//
// ROLLBACK (no code change, no redeploy):
//   In the browser DevTools console, run:
//     localStorage.setItem('cs:disableKerning','false'); location.reload();
//   To re-enable after rollback:
//     localStorage.removeItem('cs:disableKerning'); location.reload();
//
// The patch is applied once at module import. It wraps HTMLCanvasElement's
// getContext so every 2D context created after this point (including Fabric's
// lower canvas, upper canvas, hidden measuring canvas, export compositors,
// and thumbnail compositors) has fontKerning='none' set by default.

const STORAGE_KEY = 'cs:disableKerning';

function isEnabled(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v !== 'false';
  } catch {
    return true;
  }
}

export function installKerningPatch(): void {
  if (typeof HTMLCanvasElement === 'undefined') return;
  const proto = HTMLCanvasElement.prototype as HTMLCanvasElement & {
    __csKerningPatched?: boolean;
  };
  if (proto.__csKerningPatched) return;
  proto.__csKerningPatched = true;

  try {
    (window as unknown as { __csKerning?: unknown }).__csKerning = {
      disable: () => {
        localStorage.setItem(STORAGE_KEY, 'true');
        location.reload();
      },
      enable: () => {
        localStorage.setItem(STORAGE_KEY, 'false');
        location.reload();
      },
      reset: () => {
        localStorage.removeItem(STORAGE_KEY);
        location.reload();
      },
      status: () => (isEnabled() ? 'disabled (caret-aligned paint)' : 'enabled (native kerning)'),
    };
  } catch {
    // ignore
  }

  const originalGetContext = proto.getContext;
  proto.getContext = function patchedGetContext(
    this: HTMLCanvasElement,
    ...args: Parameters<HTMLCanvasElement['getContext']>
  ): ReturnType<HTMLCanvasElement['getContext']> {
    const ctx = originalGetContext.apply(this, args as any);
    if (
      ctx &&
      args[0] === '2d' &&
      isEnabled() &&
      'fontKerning' in (ctx as CanvasRenderingContext2D)
    ) {
      try {
        (ctx as CanvasRenderingContext2D).fontKerning = 'none';
      } catch {
        // browsers that don't support fontKerning silently skip
      }
    }
    return ctx;
  } as HTMLCanvasElement['getContext'];
}
